import { Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class LocaleLoaderService {
  // A map of loader functions. 
  // This avoids the "string-only" dynamic import that fails in strict ESM mode.
  private readonly localeLoaders: Record<string, () => Promise<any>> = {
    'en': () => import('@angular/common/locales/en'),
    'fr': () => import('@angular/common/locales/fr'),
    'it': () => import('@angular/common/locales/it'),
    'de': () => import('@angular/common/locales/de'),
    'es': () => import('@angular/common/locales/es'),
    'sv': () => import('@angular/common/locales/sv'),
    'hu': () => import('@angular/common/locales/hu'),
    'nl': () => import('@angular/common/locales/nl'),
    'ru': () => import('@angular/common/locales/ru'),
    'ar': () => import('@angular/common/locales/ar'),
    'zh': () => import('@angular/common/locales/zh'),
    'pt': () => import('@angular/common/locales/pt'),
    'pl': () => import('@angular/common/locales/pl'),
    'tr': () => import('@angular/common/locales/tr'),
    'ja': () => import('@angular/common/locales/ja'),
    'ko': () => import('@angular/common/locales/ko'),
    'th': () => import('@angular/common/locales/th'),
    'zh-Hant': () => import('@angular/common/locales/zh-Hant'),
    'bg': () => import('@angular/common/locales/bg'),
    'et': () => import('@angular/common/locales/et'),
    'uk': () => import('@angular/common/locales/uk'),
    'fi': () => import('@angular/common/locales/fi'),
    'sq': () => import('@angular/common/locales/sq'),
    'hr': () => import('@angular/common/locales/hr'),
    'ka': () => import('@angular/common/locales/ka'),
    'he': () => import('@angular/common/locales/he'),
    'lv': () => import('@angular/common/locales/lv'),
    'lt': () => import('@angular/common/locales/lt'),
    'ro': () => import('@angular/common/locales/ro'),
    'sk': () => import('@angular/common/locales/sk'),
    'cs': () => import('@angular/common/locales/cs'),
    'sr': () => import('@angular/common/locales/sr'),
    'el': () => import('@angular/common/locales/el'),
    'da': () => import('@angular/common/locales/da')
  };

  public loadLocale(localeId: string): Observable<boolean> {
    const loader = this.localeLoaders[localeId];

    if (!loader) {
      console.warn(`Locale ${localeId} not supported or not found in registry.`);
      return of(true);
    }

    return from(loader()).pipe(
      tap(module => {
        registerLocaleData(module.default);
      }),
      map(() => true),
      catchError(err => {
        console.error(`Failed to load locale data for: ${localeId}`, err);
        return throwError(() => err);
      })
    );
  }

  public loadMultipleLocales(): Observable<boolean> {
    const allLocaleIds = Object.keys(this.localeLoaders);
    const requests = allLocaleIds.map(id => this.loadLocale(id));

    return requests.length > 0 ? forkJoin(requests).pipe(map(() => true)): of(true);
  }
}